import React, { useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import { Upload, Download, X, FileSpreadsheet, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API, buildHeaders } from "./shared";

const REQUIRED_COLS = ["school_id", "school_name", "leader_name", "leader_role"];
const OPTIONAL_COLS = ["city", "state", "county", "website", "status", "leader_email", "leader_phone", "verified"];

const parsePreview = (text) => {
  if (!text || !text.trim()) return { ok: false, error: "CSV is empty", headers: [], schools: 0, leaders: 0, hasEmail: false, hasPhone: false };
  // Lightweight CSV header parse (does not validate every row)
  const firstLine = text.split(/\r?\n/)[0] || "";
  const headers = firstLine.split(",").map((h) => h.trim().toLowerCase());
  const missing = REQUIRED_COLS.filter((c) => !headers.includes(c));
  if (missing.length) {
    return { ok: false, error: `Missing required columns: ${missing.join(", ")}`, headers, schools: 0, leaders: 0 };
  }
  // Approx counts — count unique school_id values + total non-empty leader_name rows
  const lines = text.split(/\r?\n/).slice(1).filter((l) => l.trim());
  const sidIdx = headers.indexOf("school_id");
  const leaderIdx = headers.indexOf("leader_name");
  const uniq = new Set();
  let leaderCount = 0;
  lines.forEach((line) => {
    const cols = line.split(",");
    if (cols[sidIdx]) uniq.add(cols[sidIdx].trim());
    if (cols[leaderIdx] && cols[leaderIdx].trim()) leaderCount += 1;
  });
  return {
    ok: true,
    headers,
    schools: uniq.size,
    leaders: leaderCount,
    hasEmail: headers.includes("leader_email") || headers.includes("email"),
    hasPhone: headers.includes("leader_phone") || headers.includes("phone"),
  };
};

export default function ImportCsvModal({ open, onClose, onImported, session, campaignId, campaignName }) {
  const [csvText, setCsvText] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const headers = useMemo(() => buildHeaders(session), [session]);
  const preview = useMemo(() => parsePreview(csvText), [csvText]);

  if (!open) return null;

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv") && file.type !== "text/csv") {
      toast.error("Please choose a .csv file");
      return;
    }
    const text = await file.text();
    setCsvText(text);
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const submit = async () => {
    if (!preview.ok) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/admin/campaigns/import-csv`, { csv: csvText }, { headers });
      const d = r.data || {};
      toast.success(
        `Imported ${d.schools || 0} schools, ${d.leaders_new || 0} new + ${d.leaders_existing || 0} updated leaders`,
        { description: "Existing rows were upserted — your edits to other fields are preserved." },
      );
      onImported && onImported(d);
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  const downloadExport = async () => {
    try {
      const url = campaignId
        ? `${API}/api/admin/charter-directory/export.csv?campaign_id=${campaignId}`
        : `${API}/api/admin/charter-directory/export.csv`;
      const r = await axios.get(url, { headers, responseType: "blob" });
      const blob = new Blob([r.data], { type: "text/csv" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = campaignId ? `${(campaignName || "campaign").replace(/\W+/g, "_")}_directory.csv` : "charter_directory.csv";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="fixed inset-0 z-[115] flex items-center justify-center bg-black/55 p-3 sm:p-4" data-testid="import-csv-modal">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col max-h-[92vh]">
        <div className="px-5 py-4 flex items-center justify-between" style={{ background: "#0b1220", color: "#fff" }}>
          <div className="flex items-center gap-2 min-w-0">
            <FileSpreadsheet className="h-5 w-5 text-[#C79A3B] shrink-0" />
            <div className="min-w-0">
              <div className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-[#C79A3B]">CSV Import</div>
              <h2 className="text-[15px] sm:text-[16px] font-bold truncate" data-testid="import-csv-title">
                Import schools & leaders
              </h2>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-md hover:bg-white/10 text-white/70 hover:text-white shrink-0" data-testid="import-csv-close-btn">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 sm:px-6 py-5 overflow-y-auto flex-1 min-h-0 space-y-4">
          {/* Drag & drop area */}
          <div
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            className="rounded-xl border-2 border-dashed p-6 text-center cursor-pointer hover:bg-[#f8fafc] transition-colors"
            style={{ borderColor: "#cbd5e1" }}
            data-testid="import-csv-dropzone"
          >
            <Upload className="h-7 w-7 mx-auto mb-2 text-[#94a3b8]" />
            <div className="text-[13px] font-semibold text-[#0f172a]">Click to select or drop your .csv here</div>
            <div className="mt-1 text-[11.5px] text-[#64748b]">
              Required columns: <code>school_id, school_name, leader_name, leader_role</code>.
              Optional: <code>city, state, county, website, status, leader_email, leader_phone, verified</code>.
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="import-csv-file-input"
            />
          </div>

          {/* Paste fallback */}
          <details className="rounded-md border border-[#e5e7eb]">
            <summary
              className="cursor-pointer text-[12px] font-semibold text-[#475569] px-3 py-2 hover:bg-[#f8fafc]"
              data-testid="import-csv-paste-toggle"
            >
              …or paste CSV directly
            </summary>
            <textarea
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              rows={6}
              className="w-full px-3 py-2 text-[12px] font-mono border-t border-[#e5e7eb]"
              placeholder="school_id,school_name,city,state,county,website,status,leader_name,leader_role,leader_email,leader_phone,verified"
              data-testid="import-csv-paste-textarea"
            />
          </details>

          {/* Preview */}
          {csvText.trim() && (
            <div
              className="rounded-lg border p-3"
              style={{
                background: preview.ok ? "#f0fdf4" : "#fef2f2",
                borderColor: preview.ok ? "#bbf7d0" : "#fecaca",
              }}
              data-testid="import-csv-preview"
            >
              <div className="flex items-start gap-2.5">
                {preview.ok
                  ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-[#15803d]" />
                  : <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-[#b91c1c]" />}
                <div className="text-[12.5px] flex-1 min-w-0" style={{ color: preview.ok ? "#15803d" : "#b91c1c" }}>
                  {preview.ok ? (
                    <>
                      <div className="font-bold">Looks good — ready to import.</div>
                      <div className="mt-0.5 text-[#0f172a]">
                        <strong>{preview.schools}</strong> schools · <strong>{preview.leaders}</strong> leader rows
                        {preview.hasEmail && <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#dbeafe] text-[#1d4ed8]">email</span>}
                        {preview.hasPhone && <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#dbeafe] text-[#1d4ed8]">phone</span>}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="font-bold">CSV is missing required columns.</div>
                      <div className="mt-0.5">{preview.error}</div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Behavior notice */}
          <div className="rounded-md px-3 py-2 text-[11.5px]" style={{ background: "rgba(199,154,59,0.10)", color: "#92400e" }}>
            <strong>Upsert mode:</strong> rows with an existing <code>school_id</code> are updated. Leaders are matched on
            (school_id + leader_name + leader_role). Empty <code>leader_email</code> / <code>leader_phone</code> values
            do NOT overwrite manual edits — only non-empty incoming values replace stored ones.
          </div>
        </div>

        <div className="px-5 sm:px-6 py-3 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2 border-t" style={{ borderColor: "#e5e7eb" }}>
          <Button
            type="button"
            variant="outline"
            onClick={downloadExport}
            className="inline-flex items-center gap-1.5"
            data-testid="import-csv-export-btn"
          >
            <Download className="h-3.5 w-3.5" /> Export current directory
          </Button>
          <div className="flex items-center gap-2 justify-end">
            <Button type="button" variant="outline" onClick={onClose} data-testid="import-csv-cancel-btn">Cancel</Button>
            <Button
              type="button"
              onClick={submit}
              disabled={!preview.ok || busy}
              className="bg-[#C79A3B] hover:bg-[#a87c2a] text-white disabled:opacity-50"
              data-testid="import-csv-submit-btn"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
              Import
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
